import { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { formatTime } from '@shared/format.js';
import { api } from '../api.js';

const COLORS = ['#22D3EE', '#818CF8', '#34D399', '#FBBF24', '#FB7185', '#A78BFA'];

export default function ComparisonChart({ studentIds, event, range, studentNames }) {
  const [series, setSeries] = useState([]);

  useEffect(() => {
    if (studentIds.length === 0) {
      setSeries([]);
      return;
    }
    let cancelled = false;
    apiFetch(studentIds, event, range).then((s) => {
      if (!cancelled) setSeries(s);
    });
    return () => {
      cancelled = true;
    };
  }, [studentIds, event, range]);

  async function apiFetch(ids, ev, rg) {
    try {
      const data = await api(`/coach/comparison?event=${ev}&range=${rg}&ids=${ids.join(',')}`);
      return data.series || [];
    } catch {
      return [];
    }
  }

  const data = useMemo(() => {
    const map = new Map();
    series.forEach((s, si) => {
      const name = studentNames[s.student_id] || s.nickname || `学生${si + 1}`;
      for (const d of s.trend?.daily || []) {
        if (d.avg === null || d.avg === undefined) continue;
        if (!map.has(d.date)) map.set(d.date, { date: d.date });
        map.get(d.date)[name] = d.avg;
      }
    });
    return [...map.values()].sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [series, studentNames]);

  const lines = series.map((s, i) => ({
    name: studentNames[s.student_id] || s.nickname || `学生${i + 1}`,
    color: COLORS[i % COLORS.length],
  }));

  return (
    <div>
      {series.length === 0 ? (
        <div className="text-center text-sm text-slate-500 py-12">请选择至少一名学生进行对比</div>
      ) : (
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
              <CartesianGrid stroke="#1E2A47" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fill: '#64748B', fontSize: 11 }} tickFormatter={(v) => v.slice(5)} minTickGap={28} axisLine={{ stroke: '#334155' }} tickLine={false} />
              <YAxis reversed tick={{ fill: '#64748B', fontSize: 11 }} tickFormatter={(v) => formatTime(v)} domain={['auto', 'auto']} axisLine={{ stroke: '#334155' }} tickLine={false} width={64} />
              <Tooltip
                content={({ active, payload, label }) =>
                  active && payload?.length ? (
                    <div className="bg-ink-900 border border-slate-700 rounded-xl px-3 py-2 text-xs shadow-xl">
                      <div className="text-slate-400 mb-1">{label}</div>
                      {payload.map((p) => (
                        <div key={p.dataKey} className="flex items-center gap-2 py-0.5">
                          <span className="w-2 h-2 rounded-full" style={{ background: p.stroke }} />
                          <span className="text-slate-400">{p.dataKey}</span>
                          <span className="ml-auto font-mono text-slate-100">{formatTime(p.value)}</span>
                        </div>
                      ))}
                    </div>
                  ) : null
                }
              />
              <Legend wrapperStyle={{ fontSize: 12, color: '#94A3B8' }} />
              {lines.map((l) => (
                <Line key={l.name} type="monotone" dataKey={l.name} stroke={l.color} strokeWidth={2} dot={false} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <p className="text-[11px] text-slate-600 mt-2">* 展示各学生每日平均成绩，纵轴反转：越高越快</p>
    </div>
  );
}
